// Single source of truth about Diarmuid.
// Used by the Experience timeline, the chat assistant (client-side instant answers)
// and the Netlify chat function (system prompt), so everything stays in sync.
// Keep this file free of React so the serverless function can import it too.

import portfolio from "../JsonFolders/portfolio.json";

export const PROFILE = {
  name: "Diarmuid Hession",
  headline: "Full Stack Developer & Software Engineer",
  location: "Mullingar, Co. Westmeath, Ireland",
  email: "code@diarmuid.dev",
  links: {
    portfolio: "https://diarmuid.dev",
    github: "https://github.com/DiarmuidHe",
    linkedin: "https://www.linkedin.com/in/d-hession",
    cv: "https://diarmuid.dev/cv",
  },
  education: {
    degree: "BSc (Hons) Computing (Software), Level 8",
    school: "ATU Sligo",
    status: "Returning for his 4th (final) year in October 2026.",
    note: "Achieved First Class Honours (1:1) in 3rd year. Strongest modules: Object Oriented Development (81), Object Oriented Programming (80), Open Stack Development (77), Project 300 (74).",
    graduation: "2027",
    yearThree: {
      classification: "First Class Honours (1:1)",
      credits: { earned: 180, total: 240 },
      // Strongest 3rd-year modules, shown on the Education section
      modules: [
        { title: "Open Stack Development", grade: 77 },
        { title: "Database Programming", grade: 76 },
        { title: "Mobile Application Development", grade: 76 },
        { title: "Professional Development", grade: 75 },
        { title: "Project 300", grade: 74 },
        { title: "Web Programming 2", grade: 72 },
      ],
    },
  },
  skills: {
    Languages: ["C#", ".NET", "Python", "Java", "JavaScript", "TypeScript", "C++", "SQL"],
    "Web & Databases": ["HTML", "CSS", "React", "Angular", "Node.js", "FastAPI", "Bootstrap", "PostgreSQL", "MongoDB", "Firebase", "Netlify"],
    "Cloud & DevOps": [
      "AWS (Amplify, Lex, Bedrock, Lambda, S3, CloudWatch, DynamoDB, EC2, Route 53, IAM)",
      "Azure",
      "Docker / Docker Compose",
      "CI/CD with GitHub Actions",
      "Cloudflare",
    ],
    "AI & Computer Vision": ["PyTorch", "TensorFlow", "Ultralytics YOLO", "OpenCV"],
    "Tools & Other": ["Git", "Linux", "Visual Studio", "Unit Testing", "OpenCV", "VIAME (marine video analytics)", "FFmpeg", "Tesseract OCR", "CNN / MNIST", "ERP systems", "Twilio", "XAML"],
  },
  softSkills: [
    "Problem solving: adapts quickly to new tools and frameworks and troubleshoots integration issues",
    "Teamwork: partnered with a UX designer on website updates that refined navigation and user flow",
    "Communication: writes concise meeting reports so teams have clear follow-up actions",
    "Adaptability: balanced college with part-time work at McDonald's and volunteer mentoring at CoderDojo",
  ],
  hobbies: ["Traveling", "Reading", "Gaming", "Programming"],
  // Longer write-ups for the chat assistant, keyed by project title in portfolio.json.
  // The cards stay short; the assistant gets the full picture.
  projectDetails: {
    "AABCI - AI Automated Ballot Counter Ireland": [
      "Also called Ballot Stamp Verifier. Diarmuid has been working with an electoral commission consultant on this project. The code is on GitHub: https://github.com/AABCI-Group/Ballot-Recognition-software",
      "Built for ranked-preference elections (voters write 1, 2, 3 ... beside candidates). Given a photo or scan of a ballot, in a few seconds it answers: does the ballot carry a genuine official stamp, what number was written in each candidate's box, and is the ballot valid or does a person need to look at it. Results are saved as JSON and CSV audit files and can be written to a live election database.",
      "Stage 1, image preparation: auto-rotates ballots upright in 90-degree steps. A separate tool for rough phone photos finds the paper edges, removes the background and corrects perspective so the ballot lies flat and square.",
      "Stage 2, stamp verification: a YOLO object-detection model finds the official stamp, but a detection alone is not a pass. It must also pass geometric checks: plausible size, circular shape, outline fits an ellipse, matches a reference template, and is made of a realistic spread of ink dots. Verdicts: VALID STAMP, REVIEW REQUIRED or NO STAMP. The model was trained on thousands of synthetic ballots generated by the project, including deliberate decoy marks to teach it what a stamp is not. A built-in web labelling tool lets real scans be annotated to improve the model.",
      "Stage 3, handwritten preference reading: finds the ballot layout (each candidate row and preference box), straightens every box, isolates handwriting from the printed border and skips blank boxes. Digits are read by a TensorFlow neural network trained on EMNIST. A separate pipeline builds synthetic filled-in ballots from NIST handwriting samples for further training. Accuracy targets: at least 99% for single digits and at least 98.5% for exact rank reading.",
      "Optional AI mode sends box images to OpenAI's vision models instead, designed for privacy: only anonymised box crops are sent; candidate names, ballot IDs, filenames and election details stay local; server-side storage is turned off; every returned answer is checked locally against the allowed number range.",
      "Stage 4, validation: preferences must form a proper sequence (exactly 1, 2, 3 ... with no gaps or repeats), and numbers written outside the boxes are flagged. A ballot is Valid only when the stamp is verified, the sequence is correct and nothing is written outside the boxes. Every other ballot is marked Doubtful with a plain-English reason (e.g. 'Stamp did not validate') so officials see at a glance why it was flagged.",
      "Ways to run the same core engine: command line (one ballot or a whole folder); a phone upload server where staff scan a QR code for their ballot box and upload photos from a phone browser; a live scanner watcher that watches a CZUR document scanner's folder, processes each new scan and skips duplicates; AWS, where a Lambda function runs on every ballot image uploaded to S3 exactly once; and Docker containers for the app and the Lambda.",
      "Storage: results can go to a Supabase (PostgreSQL) database tracking each ballot paper and its individual preferences against the election's candidates. Annotated images (detected stamp, and an overlay of how the layout was read) are uploaded to S3 for audit. It also works fully offline, simply skipping the database step.",
      "Engineering highlights: two AI models working together (PyTorch/YOLO for the stamp, TensorFlow for handwriting); rules on top of the AI so a ballot is accepted only when the evidence agrees, with borderline cases escalated to a person rather than guessed; protection against double-counting via DynamoDB locks and database uniqueness rules, even with several processing routes running at once; synthetic training data generation for both stamps and handwriting; every decision comes with a confidence score, debug images and a written reason.",
      "Tested with automated tests for sequence validation, stamp geometry, the scanner watcher, the AI digit reader and database handling of repeat submissions. Built for real use end to end: an A3 print-master generator, QR codes for ballot boxes, a ballot-box location setting and deployment guides.",
      "In one sentence: it uses computer vision and deep learning to confirm a ballot's official stamp, read the voter's handwritten preferences and check the ballot is valid, sending anything uncertain to a person and keeping a full audit trail; it runs on a laptop, from a phone, beside a scanner or in the cloud.",
    ],
    "Aithne Fish Detection": [
      "Also called Fish Monitor. Turns raw underwater video, recorded or live, into counted, tracked, reviewable and species-labelled fish observations.",
      "Pipeline: video -> VIAME (Kitware's open-source marine video toolkit) running a DEIMv2 GPU fish detector with ByteTrack multi-object tracking -> CSV -> Python parser -> PostgreSQL -> FastAPI -> React dashboard / JSON / CSV.",
      "Batch analysis: upload MP4/MOV/AVI/MKV, queue jobs for one or many videos, retry failures. A 'fish passage' is a track with confidence >= 0.60 (it counts passages, not unique fish). Renders annotated MP4s and cropped clips that follow one fish each.",
      "Live monitoring: 17 public underwater cameras (a Coral City reef camera in Miami, 3 SmartBay cameras in Galway Bay, 13 Ocean Networks Canada cameras from coastal inlets to deep-sea hydrothermal vents). Analyses streams at 4 FPS up to 1080p with annotated snapshots and rolling activity stats, health-checks which cameras are broadcasting, retries with shorter chunks after detector timeouts, and records sessions into the video library.",
      "Species ID (optional): picks the clearest crops of a fish and sends them to the Fishial AI API, combining several frames by consensus voting with early stopping and a hard budget cap on paid calls. Operators can also identify any single fish on demand. Reference photos appear beside names, and a regional plausibility filter drops unlikely species. Only fish crops leave the system, never full frames or camera URLs.",
      "Human review: every track can be accepted, rejected or flagged; original detector evidence is never altered. CSV exports record provenance (pipeline, threshold, job).",
      "Architecture: three Docker Compose services (FastAPI API, batch GPU worker, optional live worker) plus PostgreSQL, which doubles as the job queue using row locks, heartbeats and a one-active-job-per-video index, so no Celery or Redis. A mock mode runs the full stack without a GPU.",
      "Frontend: React 19, TypeScript, Vite, TanStack Query, React Router, hand-written SVG charts. Screens: overview, library, video detail, review queue, live, species, system.",
      "Security: UUID file names, path checks, cameras chosen only from an approved registry (never by URL, preventing SSRF), CSV formula-injection protection.",
      "Engineering: about 30 pytest modules that run without a GPU, benchmark scripts, and documented tuning of detector thresholds and sampling rates on real footage. FUnIE-GAN underwater image enhancement is built in but off until labelled evaluation shows it helps.",
    ],
  },
  extraProjects: [
    {
      title: "Optical Ballot Recognition",
      description: "An OCR solution (Python, Tesseract, OpenCV, a CNN trained on MNIST) that extracts and structures data from ballot paper images.",
    },
  ],
};

// Course completion certificates shared by the site and CV.
export const CERTIFICATES = [
  {
    slug: "agile-software-requirements",
    title: "Agile Software Projects: Software Requirements",
    shortTitle: "Agile requirements",
    issuer: "Skillsoft",
    completed: "2026-07-23",
    detail: "Training credit value: 1.25",
  },
  {
    slug: "system-testing-reliability",
    title: "Decision Analysis and Management: System Testing & Reliability",
    shortTitle: "System testing",
    issuer: "Department of Social Protection",
    completed: "2026-07-23",
    detail: "Duration: 1 hr 22 min",
  },
  {
    slug: "git-devops-workflows",
    title: "Using Git for DevOps: Managing Conflict & Effectively Using Git Workflow",
    shortTitle: "Git workflows",
    issuer: "Department of Social Protection",
    completed: "2026-04-23",
    detail: "Duration: 58 min",
  },
  {
    slug: "working-with-git",
    title: "Working with Git",
    shortTitle: "Working with Git",
    issuer: "Department of Social Protection",
    completed: "2026-04-22",
    detail: "Duration: 1 hr 7 min",
  },
];

// type: "tech" roles are software/IT; "other" roles show transferable skills.
// Dates are "YYYY-MM". A role is current until the end of its end month; end: null means no end date yet.
// finished: true marks a role as over before its end month is out.
// Optional stages show progression within one employer.
export const EXPERIENCE = [
  {
    id: "dsp",
    role: "Software Engineer",
    company: "Department of Social Protection",
    type: "tech",
    icon: "laptop",
    start: "2026-05",
    end: "2026-10",
    finished: true,
    stages: [
      { title: "Software Engineer Intern", start: "2026-05", end: "2026-07" },
      { title: "Software Engineer", start: "2026-08", end: "2026-10" },
    ],
    location: "County Sligo, Ireland",
    workplace: "Hybrid",
    highlights: [
      "Joined as a software engineering intern for three months, then stayed on as a Software Engineer.",
      "Worked on MyWelfare at the Department of Social Protection.",
      "Finished in October 2026 to return to college for 4th year.",
    ],
    skills: ["Software Infrastructure", "Software Design"],
    // Names match the logos in portfolio.json, so the card can show them
    stack: ["C#", ".NET", "Angular", "TypeScript", "SQL", "Azure"],
  },
  {
    id: "qtp",
    role: "IT Administrator",
    company: "Quality Tractor Parts Ltd.",
    employmentType: "Full-time",
    type: "tech",
    icon: "tractor",
    start: "2025-05",
    end: "2025-09",
    location: "Mullingar, County Westmeath, Ireland",
    workplace: "On-site",
    highlights: [
      "Automated 3+ repetitive tasks with Python scripts, saving 10+ hours a week.",
      "Worked in Agile sprints, joining scrums and sprint planning and completing 12+ tickets per sprint.",
      "Helped roll out a 2FA system securing 1,000+ customer accounts.",
      "Brought IT ticket resolution time down to under 24 hours.",
    ],
    skills: ["Python", "Automation", "Agile", "User Interface Design", "Policy Analysis"],
  },
  {
    id: "coderdojo",
    role: "Coding Mentor",
    company: "CoderDojo",
    employmentType: "Part-time · Volunteer",
    type: "tech",
    icon: "teach",
    start: "2024-09",
    end: "2025-09",
    location: "Mullingar, County Westmeath, Ireland",
    workplace: "On-site",
    highlights: [
      "Taught coding basics to groups of 10–15 students, adapting to diverse learning styles.",
      "Collaborated with 5+ mentors to design engaging sessions, improving student retention.",
    ],
    skills: ["Teaching", "Mentoring", "Communication"],
  },
  {
    id: "doj",
    role: "Counter, Poll Clerk & Tallier",
    company: "Department of Justice",
    employmentType: "Seasonal",
    type: "other",
    icon: "vote",
    start: "2024-03",
    end: "2024-11",
    location: "County Westmeath, Ireland",
    workplace: "On-site",
    highlights: [
      "As Poll Clerk, supported the smooth running of polling stations by assisting voters and ensuring compliance with electoral procedures.",
      "Counted and tallied votes accurately as part of the count team.",
    ],
    skills: ["Communication", "Teamwork", "Attention to Detail"],
  },
  {
    id: "mcdonalds",
    role: "Crew Member",
    company: "McDonald's",
    employmentType: "Part-time",
    type: "other",
    icon: "food",
    start: "2022-07",
    end: "2025-05",
    location: "Mullingar, Ireland",
    workplace: "On-site",
    highlights: [
      "Delivered efficient service in a fast-paced environment serving 300+ customers daily.",
      "Worked closely with team members to maintain smooth operations and meet targets.",
    ],
    skills: ["Teamwork", "Working Under Pressure", "Customer Service"],
  },
];

// ---------- Date helpers ----------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseYM(ym) {
  const [y, m] = ym.split("-").map(Number);
  return { y, m };
}

export function formatMonth(ym) {
  if (!ym) return "Present";
  const { y, m } = parseYM(ym);
  return `${MONTHS[m - 1]} ${y}`;
}

// Inclusive month count, matching how LinkedIn reports durations (May–Sep = 5 mos).
export function durationLabel(start, end, now = new Date()) {
  const s = parseYM(start);
  const e = end ? parseYM(end) : { y: now.getFullYear(), m: now.getMonth() + 1 };
  const total = Math.max(1, (e.y - s.y) * 12 + (e.m - s.m) + 1);
  const years = Math.floor(total / 12);
  const months = total % 12;
  const parts = [];
  if (years) parts.push(`${years} yr${years > 1 ? "s" : ""}`);
  if (months) parts.push(`${months} mo${months > 1 ? "s" : ""}`);
  return parts.join(" ");
}

// A role counts as current until its end month is over
export function isCurrent(job, now = new Date()) {
  if (job.finished) return false;
  if (!job.end) return true;
  const { y, m } = parseYM(job.end);
  return now.getFullYear() * 12 + now.getMonth() + 1 <= y * 12 + m;
}

export function dateRange(job) {
  return `${formatMonth(job.start)} – ${formatMonth(job.end)}`;
}

// URL-safe id for a project, used by the /projects/:slug case-study links
export function projectSlug(title) {
  return title
    .toLowerCase()
    .split(" - ")[0]
    .replace(/[^a-z0-9#]+/g, "-")
    .replace(/#/g, "sharp")
    .replace(/^-+|-+$/g, "");
}

export const PROJECTS = [
  ...portfolio.images.projects.map(({ title, description, link, highlight, linkNote }) => ({
    title,
    slug: projectSlug(title),
    description,
    highlight: highlight || null,
    link: link && link !== "#" ? link : null,
    linkNote: linkNote || null,
    details: PROFILE.projectDetails[title] || null,
  })),
  ...PROFILE.extraProjects.map((p) => ({ ...p, link: null })),
];

// Compact plain-text knowledge base for the LLM system prompt.
// Kept terse on purpose: every token here is paid for on every request.
export function buildKnowledgeBase(now = new Date()) {
  const p = PROFILE;
  const jobs = EXPERIENCE.map(
    (j) =>
      `- ${j.role} at ${j.company}${j.employmentType ? ` (${j.employmentType})` : ""}, ${dateRange(j)} [${durationLabel(j.start, j.end, now)}]${j.stages ? ` (progression: ${j.stages.map((s) => `${s.title} ${formatMonth(s.start)}–${formatMonth(s.end)}`).join(", then ")})` : ""}, ${j.location}, ${j.workplace}${isCurrent(j, now) ? ` (CURRENT ROLE${j.end ? `, finishing ${formatMonth(j.end)}` : ""})` : ""}. ${j.highlights.join(" ")} Skills: ${j.skills.join(", ")}.${j.stack ? ` Tech stack: ${j.stack.join(", ")}.` : ""}`
  ).join("\n");
  const projects = PROJECTS.map((pr) => {
    const details = pr.details ? `\n${pr.details.map((d) => `  - ${d}`).join("\n")}` : "";
    const page = pr.slug ? ` Case study: ${p.links.portfolio}/projects/${pr.slug}` : "";
    return `- ${pr.title}:${page} ${pr.highlight ? `${pr.highlight} ` : ""}${pr.description}${pr.link ? ` Link: ${pr.link}` : pr.linkNote ? ` Link: ${pr.linkNote}.` : ""}${details}`;
  }).join("\n");
  const skills = Object.entries(p.skills)
    .map(([k, v]) => `- ${k}: ${v.join(", ")}`)
    .join("\n");

  return `NAME: ${p.name}
HEADLINE: ${p.headline}
BASED IN: ${p.location}
EDUCATION: ${p.education.degree}, ${p.education.school}. ${p.education.status} ${p.education.note}

EXPERIENCE (newest first):
${jobs}

PROJECTS:
${projects}

TECHNICAL SKILLS:
${skills}

SOFT SKILLS:
${p.softSkills.map((s) => `- ${s}`).join("\n")}

HOBBIES: ${p.hobbies.join(", ")}
COMING TO CODE: Started with Scratch for the Junior Cert, went deeper with Python for the Leaving Cert.

LINKS:
- Email: ${p.email}
- GitHub: ${p.links.github}
- LinkedIn: ${p.links.linkedin}
- CV: ${p.links.cv}
- Contact form: ${p.links.portfolio}/#contact
- Experience timeline: ${p.links.portfolio}/#experience`;
}
